import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface BulkActionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: "approve" | "reject" | "create-rule" | "mass-edit" | null;
  selectedCount: number;
  selectedIds: string[];
  onSuccess?: () => void;
}

export const BulkActionsDialog = ({
  open,
  onOpenChange,
  action,
  selectedCount,
  selectedIds,
  onSuccess,
}: BulkActionsDialogProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [accountNumber, setAccountNumber] = useState("");
  const [reason, setReason] = useState("");
  const [ruleName, setRuleName] = useState("");
  const [rulePattern, setRulePattern] = useState("");

  const handleApprove = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from("transactions")
        .update({ human_verified: true, needs_review: false })
        .in("id", selectedIds);

      if (error) throw error;

      toast({
        title: "Approved Successfully",
        description: `${selectedCount} transaction(s) have been approved.`,
      });
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to approve transactions.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!reason.trim()) {
      toast({
        title: "Reason Required",
        description: "Please provide a reason for rejection.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from("transactions")
        .update({ 
          needs_review: true, 
          ai_classification: { rejected: true, reason } 
        })
        .in("id", selectedIds);

      if (error) throw error;

      toast({
        title: "Rejected Successfully",
        description: `${selectedCount} transaction(s) have been marked for review.`,
      });
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reject transactions.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setReason("");
    }
  };

  const handleCreateRule = async () => {
    if (!ruleName.trim() || !rulePattern.trim() || !accountNumber.trim()) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields to create a rule.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data: orgData } = await supabase.auth.getUser();
      const { data: userData } = await supabase
        .from("users")
        .select("organization_id")
        .eq("id", orgData.user?.id)
        .single();

      if (!userData?.organization_id) throw new Error("Organization not found");

      const { error } = await supabase.from("ai_learning_patterns").insert({
        organization_id: userData.organization_id,
        description_pattern: rulePattern,
        account: accountNumber,
        counterparty: "",
        learned_from: "manual_rule",
        confidence: 100,
        vat_rate: 0,
      });

      if (error) throw error;

      toast({
        title: "Rule Created",
        description: `New classification rule "${ruleName}" has been created.`,
      });
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create rule.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setRuleName("");
      setRulePattern("");
      setAccountNumber("");
    }
  };

  const handleMassEdit = async () => {
    if (!accountNumber.trim()) {
      toast({
        title: "Account Required",
        description: "Please select an account number.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from("transactions")
        .update({ account_number: accountNumber, human_verified: true })
        .in("id", selectedIds);

      if (error) throw error;

      toast({
        title: "Updated Successfully",
        description: `${selectedCount} transaction(s) have been updated.`,
      });
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update transactions.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setAccountNumber("");
    }
  };

  const getDialogContent = () => {
    switch (action) {
      case "approve":
        return {
          title: "Approve Transactions",
          description: `Are you sure you want to approve ${selectedCount} transaction(s)? This will mark them as verified.`,
          content: null,
          action: handleApprove,
          actionLabel: "Approve",
        };

      case "reject":
        return {
          title: "Reject Transactions",
          description: `Provide a reason for rejecting ${selectedCount} transaction(s):`,
          content: (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reason">Reason for Rejection</Label>
                <Textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Enter reason for rejection..."
                  className="min-h-[100px]"
                />
              </div>
            </div>
          ),
          action: handleReject,
          actionLabel: "Reject",
          variant: "destructive" as const,
        };

      case "create-rule":
        return {
          title: "Create Classification Rule",
          description: "Create an AI learning pattern based on the selected transactions:",
          content: (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="ruleName">Rule Name</Label>
                <Input
                  id="ruleName"
                  value={ruleName}
                  onChange={(e) => setRuleName(e.target.value)}
                  placeholder="e.g., Office Supplies Rule"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pattern">Description Pattern</Label>
                <Input
                  id="pattern"
                  value={rulePattern}
                  onChange={(e) => setRulePattern(e.target.value)}
                  placeholder="e.g., Office, Supplies, Stationery"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="account">Target Account</Label>
                <Input
                  id="account"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="e.g., 6300"
                />
              </div>
            </div>
          ),
          action: handleCreateRule,
          actionLabel: "Create Rule",
        };

      case "mass-edit":
        return {
          title: "Mass Edit Transactions",
          description: `Update the account for ${selectedCount} transaction(s):`,
          content: (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="massAccount">Account Number</Label>
                <Input
                  id="massAccount"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="Enter account number..."
                />
              </div>
            </div>
          ),
          action: handleMassEdit,
          actionLabel: "Update",
        };

      default:
        return null;
    }
  };

  const dialogContent = getDialogContent();
  if (!dialogContent) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>{dialogContent.title}</DialogTitle>
          <DialogDescription>{dialogContent.description}</DialogDescription>
        </DialogHeader>
        {dialogContent.content}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant={dialogContent.variant || "default"}
            onClick={dialogContent.action}
            disabled={loading}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {dialogContent.actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
